/**
 * Pure-function increment decision for the PWA app-icon badge counter.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BadgeCountPolicy.swift` exactly.
 *
 * THREAT MODEL
 *   The badge counter is a side channel observable from the lock screen / PWA
 *   icon. We do not want it to:
 *
 *   - Leak the existence of muted threads. A user who has muted a spam-flag
 *     thread expects the badge NOT to bounce on every incoming message.
 *   - Surface control-plane traffic. Receipts, tombstones, and other control
 *     messages do not represent a user-visible event; they should never bump
 *     the badge.
 *   - Surface messages from blocked senders. A blocked sender's inbound is
 *     decrypted (so the ratchet advances — see BlockEnforcementPolicy) but
 *     should be invisible at every side-channel including the badge.
 *
 *   The policy is a pure function so the self-test can exhaustively cover
 *   every (flag-combination × decision) pair without touching disk or
 *   singletons. There is exactly one increment site (`BadgeCountStore.increment`)
 *   and the Service Worker push handler is required to consult this policy first.
 *
 * PRECEDENCE (top-to-bottom — most specific reason wins)
 *   1. control message  — never user-facing
 *   2. blocked sender   — security boundary, takes precedence over mute
 *   3. muted (thread or delivery) — user preference
 *   4. otherwise → increment
 *
 *   The "blocked precedence over muted" choice mirrors Signal: a user who
 *   blocked someone does NOT want a "muted" log line in the audit trail; the
 *   right reason is "blocked."
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs. Pure logic only.
 */

/**
 * Inputs to the badge-increment decision. Every flag is a pure boolean the
 * caller has already computed elsewhere (block-list lookup, per-thread mute
 * config, control-message sentinel detection, etc.).
 *
 * Mirrors iOS `BadgeIncrementContext`.
 */
export interface BadgeIncrementContext {
  /** Per-conversation mute toggle: the user has explicitly muted this thread. */
  readonly isThreadMuted: boolean;
  /** True when the decrypted plaintext is a control message (receipt,
   *  tombstone, draft sync, role change, etc.) rather than a user-visible
   *  chat bubble. */
  readonly isControlMessage: boolean;
  /** True when the envelope's authenticated sender is in the block list.
   *  Takes precedence over mute flags. */
  readonly isBlockedSender: boolean;
}

/**
 * Outcome of the policy. The caller bumps the counter only on `'increment'`;
 * the three skip cases are split so logs and metrics can pinpoint WHY the
 * badge did not move.
 *
 * Mirrors iOS `BadgeIncrementDecision`.
 */
export type BadgeIncrementDecision =
  | { readonly kind: "increment" }
  | { readonly kind: "skipMuted" }
  | { readonly kind: "skipBlocked" }
  | { readonly kind: "skipControlMessage" };

/** Singleton decision values (avoids allocating new objects on every call). */
const INCREMENT: BadgeIncrementDecision = { kind: "increment" };
const SKIP_MUTED: BadgeIncrementDecision = { kind: "skipMuted" };
const SKIP_BLOCKED: BadgeIncrementDecision = { kind: "skipBlocked" };
const SKIP_CONTROL: BadgeIncrementDecision = { kind: "skipControlMessage" };

/**
 * Returns whether the badge should be incremented for the given notification
 * context. See file header for the precedence rules.
 *
 * This is a pure function — no I/O, no side effects, safe to call from any
 * context including a Service Worker.
 *
 * Mirrors iOS `BadgeCountPolicy.shouldIncrement(_:)`.
 */
export function shouldIncrement(
  ctx: BadgeIncrementContext,
): BadgeIncrementDecision {
  // 1. Control messages — never user-facing.
  if (ctx.isControlMessage) {
    return SKIP_CONTROL;
  }
  // 2. Blocked sender — security boundary; takes precedence over mute so the
  //    audit log shows the right reason (blocked, not muted).
  if (ctx.isBlockedSender) {
    return SKIP_BLOCKED;
  }
  // 3. Muted thread — user preference.
  if (ctx.isThreadMuted) {
    return SKIP_MUTED;
  }
  // 4. Normal user-facing message.
  return INCREMENT;
}

/**
 * Convenience alias: `shouldIncrement(ctx).kind === 'increment'`.
 * Useful when the caller only needs a boolean and not the reason.
 */
export function shouldIncrementBool(ctx: BadgeIncrementContext): boolean {
  return shouldIncrement(ctx).kind === "increment";
}
